// Mock Data for Philcan Industries Website
// This file contains all configurable content and data

export const companyInfo = {
  name: "Philcan Industries Inc.",
  tagline: "Consulting & Process Improvement",
  yearsInBusiness: 15,
  phone: "+1 (587) 429 4765",
  email: "projects@philcanindustries.com",
  address: "Calgary, Alberta, Canada",
  logo: "https://customer-assets.emergentagent.com/job_process-experts-4/artifacts/sgq6gbdp_PhilCanOriginal.png"
};

export const stats = [
  { label: "Years of Experience", value: "15+" },
  { label: "Projects Completed", value: "500+" },
  { label: "Client Satisfaction", value: "98%" },
  { label: "Industries Served", value: "20+" }
];

export const services = {
  industries: [
    {
      title: "Process Improvement",
      description: "Streamline operations, reduce waste, and enhance productivity through proven methodologies.",
      icon: "Settings"
    },
    {
      title: "Operational Excellence",
      description: "Transform your organisation with sustainable practices that drive continuous improvement.",
      icon: "TrendingUp"
    },
    {
      title: "Strategic Consulting",
      description: "Expert guidance to align your processes with business objectives and market demands.",
      icon: "Target"
    },
    {
      title: "Change Management",
      description: "Navigate organisational change with confidence through structured approaches and stakeholder engagement.",
      icon: "Users"
    }
  ],
  innovation: [
    {
      title: "SR&ED Tax Credits",
      description: "Recover up to 64% of qualified innovation expenditures through Canada's Scientific Research & Experimental Development program.",
      icon: "DollarSign"
    },
    {
      title: "Innovation Funding",
      description: "Access grants and incentives for your research, development, and commercialisation projects.",
      icon: "Lightbulb"
    },
    {
      title: "Project Discovery",
      description: "We identify qualifying innovation activities you may not have realised were eligible for funding.",
      icon: "Search"
    },
    {
      title: "Documentation Support",
      description: "Comprehensive support for technical narratives, project charters, and CRA submissions.",
      icon: "FileText"
    }
  ]
};

export const processSteps = {
  industries: [
    {
      step: 1,
      title: "Discovery & Assessment",
      description: "We analyse your current operations, identify inefficiencies, and understand your business goals."
    },
    {
      step: 2,
      title: "Strategy Development",
      description: "Our team develops a customised improvement plan aligned with your objectives and resources."
    },
    {
      step: 3,
      title: "Implementation",
      description: "We work alongside your team to execute changes, providing training and support throughout."
    },
    {
      step: 4,
      title: "Continuous Improvement",
      description: "Ongoing monitoring and refinement to ensure sustainable results and lasting transformation."
    }
  ],
  innovation: [
    {
      step: 1,
      title: "Innovation Discovery",
      description: "Deep-dive sessions to uncover all qualifying innovation projects and activities within your organisation."
    },
    {
      step: 2,
      title: "Project Audit & Charter",
      description: "Detailed analysis and documentation of each project's technical uncertainties and advancements."
    },
    {
      step: 3,
      title: "Claim Preparation",
      description: "Technical narrative development, financial compilation, and form preparation for CRA submission."
    },
    {
      step: 4,
      title: "Ongoing Support",
      description: "Documentation improvements, CRA communication support, and next-year planning."
    }
  ]
};

export const industries = [
  { name: "Manufacturing", description: "Process optimisation, quality improvements, and automation integration." },
  { name: "Agriculture & Food Processing", description: "Yield improvements, processing innovations, and safety enhancements." },
  { name: "Information Technology", description: "Software development, system integration, and digital transformation." },
  { name: "Engineering & Construction", description: "Design innovations, material testing, and structural improvements." },
  { name: "Oil & Gas", description: "Extraction technologies, environmental solutions, and operational efficiency." },
  { name: "Mining & Resources", description: "Extraction methods, processing improvements, and safety innovations." },
  { name: "Medical & Life Sciences", description: "Device development, process improvements, and regulatory compliance." },
  { name: "Aerospace & Defence", description: "Component development, testing protocols, and manufacturing processes." }
];

export const testimonials = [
  {
    quote: "Philcan helped us identify significant process improvements that reduced our production costs by 23%. Their expertise in operational excellence is unmatched.",
    author: "Operations Director",
    company: "Manufacturing Company, Ontario"
  },
  {
    quote: "We had no idea our continuous improvement work qualified for SR&ED. Philcan's team found over $180,000 in eligible credits we were leaving on the table.",
    author: "CEO",
    company: "Food Processing Company, Alberta"
  },
  {
    quote: "The process improvement initiatives implemented by Philcan transformed our operations. We've seen a 40% reduction in waste and significantly improved throughput.",
    author: "Plant Manager",
    company: "Industrial Manufacturer, British Columbia"
  }
];

export const faqs = [
  {
    question: "What is SR&ED?",
    answer: "The Scientific Research and Experimental Development (SR&ED) program is a Canadian federal tax incentive program that encourages businesses to conduct research and development in Canada. It provides tax credits for eligible R&D expenditures."
  },
  {
    question: "Does my company qualify for SR&ED?",
    answer: "Many companies performing innovation work qualify without realising it. If you're solving technical problems, improving processes, or developing new products, you may be eligible. Our discovery process helps identify qualifying activities."
  },
  {
    question: "How much can I recover through SR&ED?",
    answer: "Canadian-Controlled Private Corporations (CCPCs) can recover up to 64% of eligible salary expenditures, 42% of materials, and 32% of Canadian contractor costs. Use our calculator for a personalised estimate."
  },
  {
    question: "What types of projects qualify?",
    answer: "Projects involving technological uncertainty, systematic investigation, and technological advancement may qualify. This includes process improvements, product development, software development, and more."
  },
  {
    question: "How long does the SR&ED claim process take?",
    answer: "The typical timeline from discovery to CRA submission is 4-8 weeks. CRA processing times vary but generally range from 60-120 days for refund claims."
  }
];

// SR&ED Calculator Configuration
// Tax rates are configurable and can be updated when CRA/provincial rules change
export const calculatorConfig = {
  federalRates: {
    ccpc: {
      enhanced: {
        rate: 0.35,
        refundable: true,
        expenditureLimit: 3000000
      },
      standard: {
        rate: 0.15,
        refundable: false
      }
    },
    nonCcpc: {
      rate: 0.15,
      refundable: false
    }
  },
  provincialRates: {
    AB: { name: "Alberta", rate: 0.10, refundable: true },
    BC: { name: "British Columbia", rate: 0.10, refundable: true },
    MB: { name: "Manitoba", rate: 0.00, refundable: false },
    NB: { name: "New Brunswick", rate: 0.15, refundable: true },
    NL: { name: "Newfoundland and Labrador", rate: 0.15, refundable: true },
    NS: { name: "Nova Scotia", rate: 0.15, refundable: true },
    NT: { name: "Northwest Territories", rate: 0.00, refundable: false },
    NU: { name: "Nunavut", rate: 0.00, refundable: false },
    ON: { name: "Ontario", rate: 0.10, refundable: true },
    PE: { name: "Prince Edward Island", rate: 0.00, refundable: false },
    QC: { name: "Quebec", rate: 0.14, refundable: true },
    SK: { name: "Saskatchewan", rate: 0.10, refundable: true },
    YT: { name: "Yukon", rate: 0.15, refundable: true }
  },
  expenditureRates: {
    salaries: {
      label: "Innovation Salaries",
      maxRate: 0.64,
      conservativeMultiplier: 0.75,
      description: "T4-eligible labour directly involved in innovation projects"
    },
    materials: {
      label: "Innovation Materials",
      maxRate: 0.42,
      conservativeMultiplier: 0.70,
      description: "Materials consumed or transformed in innovation projects"
    },
    contractors: {
      label: "Canadian Contractors",
      maxRate: 0.32,
      conservativeMultiplier: 0.70,
      description: "Canadian subcontractor costs supporting innovation projects"
    }
  },
  disclaimer: "This calculator provides estimates only and does not constitute tax advice. Actual SR&ED credits depend on CRA eligibility assessment and detailed analysis. Results are not a final determination of SR&ED eligibility."
};

export const provinces = [
  { code: "AB", name: "Alberta" },
  { code: "BC", name: "British Columbia" },
  { code: "MB", name: "Manitoba" },
  { code: "NB", name: "New Brunswick" },
  { code: "NL", name: "Newfoundland and Labrador" },
  { code: "NS", name: "Nova Scotia" },
  { code: "NT", name: "Northwest Territories" },
  { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" },
  { code: "PE", name: "Prince Edward Island" },
  { code: "QC", name: "Quebec" },
  { code: "SK", name: "Saskatchewan" },
  { code: "YT", name: "Yukon" }
];

export const companyTypes = [
  { value: "ccpc", label: "Canadian-Controlled Private Corporation (CCPC)" },
  { value: "non-ccpc", label: "Non-CCPC / Public Company" },
  { value: "not-sure", label: "Not Sure" }
];
